/**
 * EditorialItemReview.tsx — FR-017 (V2/V3/V4) / FR-018 / T320.
 *
 * Bindings:
 *   - api.assetQueries.getAssetById — REAL query (convex/assetQueries.ts:21);
 *     the editorial-review surface reads the asset that the inbox sends to
 *     review.
 *
 * The accept / reject actions are presented as the operator-facing decision
 * buttons. The underlying acceptance mutation is the existing editorial
 * accept-ingestion path (convex/editorial/acceptPendingIngestion.ts); when
 * the wiring picks up that mutation here it MUST route through
 * useMutationWithToast (FR-013 H1) — for now the surface emits no live
 * mutation, surfacing the buttons without a false-success path.
 *
 * Glossary-clean copy via src/i18n/operator-copy.ts.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  Breadcrumbs,
  Btn,
  EmptyState,
  Field,
  Input,
  PageHead,
  Panel,
  Skeleton,
  Thumb,
} from '../../components/primitives';
import { EDITORIAL_REVIEW_COPY } from '../../i18n/operator-copy';
// T318 — the mock fixture import retired; the review surface reads the asset
// via `api.assetQueries.getAssetById` and renders honest skeleton / empty
// states. No fixture dependency remains.
import { fmtDur, ago } from '../../lib/format';
import { useState } from 'react';

interface EditorialItemReviewProps {
  itemId?: string;
}

interface AssetShape {
  _id?: string;
  title?: string;
  source?: string;
  durationMs?: number;
  addedAtMs?: number;
}

export function EditorialItemReview({ itemId }: EditorialItemReviewProps) {
  const [comment, setComment] = useState('');

  // Real Convex subscription on the single asset being reviewed. The query
  // is `skip`-gated when no id is supplied so the empty-state branch fires
  // without a network round-trip.
  const asset = useQuery(
    api.assetQueries.getAssetById,
    itemId ? { assetId: itemId as any } : 'skip',
  ) as AssetShape | null | undefined;

  // No item id at all — surface the not-found state.
  if (!itemId) {
    return (
      <div>
        <PageHead title={EDITORIAL_REVIEW_COPY.pageTitle} />
        <Panel>
          <EmptyState
            icon="search"
            title={EDITORIAL_REVIEW_COPY.notFoundTitle}
            body={EDITORIAL_REVIEW_COPY.notFoundBody}
          />
        </Panel>
      </div>
    );
  }

  // Loading state.
  if (asset === undefined) {
    return (
      <div>
        <PageHead title={EDITORIAL_REVIEW_COPY.pageTitle} />
        <Skeleton h={320} />
      </div>
    );
  }

  // Honest-empty when the asset is missing / was removed.
  if (asset === null) {
    return (
      <div>
        <PageHead title={EDITORIAL_REVIEW_COPY.pageTitle} />
        <Panel>
          <EmptyState
            icon="search"
            title={EDITORIAL_REVIEW_COPY.notFoundTitle}
            body={EDITORIAL_REVIEW_COPY.notFoundBody}
          />
        </Panel>
      </div>
    );
  }

  const title = asset.title ?? 'Sin título';
  const source = asset.source ?? '—';
  const duration = fmtDur(asset.durationMs ?? 0);
  const agoLabel = asset.addedAtMs != null ? ago(asset.addedAtMs) : '—';

  const metaRows: Array<readonly [string, string]> = [
    ['Fuente', source],
    ['Duración', duration],
    ['Título original', title],
    ['Recibido', agoLabel],
  ];

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: EDITORIAL_REVIEW_COPY.breadcrumbBack, href: '#/editorial-inbox' },
          { label: title },
        ]}
      />
      <PageHead title={title} />
      <div className="circa-cols-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Vista previa" pad={0}>
            <div style={{ padding: 20 }}>
              <Thumb radius={14} />
            </div>
          </Panel>
          <Panel title={EDITORIAL_REVIEW_COPY.commentLabel}>
            <Field label={EDITORIAL_REVIEW_COPY.commentLabel}>
              <Input
                value={comment}
                onChange={setComment}
                placeholder={EDITORIAL_REVIEW_COPY.commentPlaceholder}
              />
            </Field>
          </Panel>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Información">
            {metaRows.map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '8px 0',
                  borderBottom: '1px solid var(--lightest-gray)',
                  fontSize: 13,
                }}
              >
                <span style={{ color: 'var(--muted-slate)' }}>{k}</span>
                <span style={{ textAlign: 'right' }}>{v}</span>
              </div>
            ))}
          </Panel>
          <Panel title="Decisión">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Btn variant="solid" full icon="check">
                {EDITORIAL_REVIEW_COPY.actionAccept}
              </Btn>
              <Btn variant="danger" full>
                {EDITORIAL_REVIEW_COPY.actionReject}
              </Btn>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export default EditorialItemReview;
